/*
  Arrows for every configurable slider on the commercial site.

  The sliders themselves are plain CSS scroll-snap rows, so they already work
  with a trackpad, a touch screen and the keyboard. What they lacked was any
  sign that they scroll at all on a desktop with a mouse, which is what the
  arrows are for.

  Nothing is added in Liquid: this walks the page, wraps each slider and injects
  its own controls, so a section only has to opt into the slider class. Arrows
  appear only while the row actually overflows, which is what makes a slider
  that is set to mobile only leave desktop untouched.
*/
(() => {
  const SELECTOR = '.c-slider, .c-slider--desktop-only, [data-scroll-arrows]';
  const WRAPPED = 'data-slider-wrapped';

  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const arrow = (dir, label) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `c-sliderwrap__nav c-sliderwrap__nav--${dir}`;
    button.setAttribute('aria-label', label);
    button.innerHTML =
      '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" width="18" height="18">' +
      '<path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="2" ' +
      'stroke-linecap="round" stroke-linejoin="round"/></svg>';
    return button;
  };

  const step = (slider) => {
    // A wide table has one child, so a "card" is most of the visible width.
    if (slider.hasAttribute('data-scroll-arrows')) return slider.clientWidth * 0.8;
    const first = slider.firstElementChild;
    if (!first) return slider.clientWidth;
    const gap = parseFloat(getComputedStyle(slider).columnGap) || 0;
    return first.getBoundingClientRect().width + gap;
  };

  const setup = (slider) => {
    if (slider.hasAttribute(WRAPPED)) return;
    slider.setAttribute(WRAPPED, '');

    // One element takes the slider's place in whatever layout it sits in, so
    // a slider that is a grid item stays one grid item. Inside it, the stage
    // holds the row and its arrows (so the arrows centre on the row), and the
    // counter sits under the stage.
    const wrap = document.createElement('div');
    wrap.className = 'c-sliderwrap';
    // A scrolling table gets a smaller arrow, no taller than one of its rows.
    if (slider.hasAttribute('data-scroll-arrows')) wrap.classList.add('c-sliderwrap--small');
    const stage = document.createElement('div');
    stage.className = 'c-sliderwrap__stage';
    slider.parentNode.insertBefore(wrap, slider);
    wrap.appendChild(stage);
    stage.appendChild(slider);

    const prev = arrow('prev', 'Previous');
    const next = arrow('next', 'Next');
    // Where you are in the row, "2 / 6". Polite, so a screen reader hears the
    // new position after a swipe without it interrupting anything.
    const count = document.createElement('p');
    count.className = 'c-sliderwrap__count';
    count.setAttribute('aria-live', 'polite');
    // Rows of text-only cards have no photo to hang the arrows on, and the
    // middle of the row is body copy, so on a phone they go beside the counter
    // like the team rows do. Tables keep their own small arrows.
    const firstSlide = slider.firstElementChild;
    const textOnly = !slider.hasAttribute('data-scroll-arrows')
      && !!firstSlide && !firstSlide.querySelector('img, video, .c-placeholder');
    const beside = slider.dataset.nav === 'beside-count' || textOnly;
    const bar = document.createElement('div');
    bar.className = 'c-sliderwrap__bar';
    const mobileNav = window.matchMedia('(max-width: 749px)');

    const placeNav = () => {
      // Team carousels keep the arrows over the photos on desktop, and move
      // them to either side of the 1/3 counter on a phone.
      if (beside && mobileNav.matches) {
        wrap.classList.add('c-sliderwrap--beside');
        bar.append(prev, count, next);
        wrap.append(bar);
        return;
      }
      wrap.classList.remove('c-sliderwrap--beside');
      stage.append(prev, next);
      if (!slider.hasAttribute('data-no-count')) wrap.append(count);
      bar.remove();
    };
    placeNav();
    mobileNav.addEventListener('change', placeNav);

    // Cards with a photo get their arrows centred on the photo, not on the
    // whole card with its text underneath. Rows without one (and scrolling
    // tables, which place their own small arrows) keep the stage centre.
    const placeArrows = () => {
      if (wrap.classList.contains('c-sliderwrap--small')) return;
      const first = slider.firstElementChild;
      const media = first && Array.from(first.querySelectorAll('img, video, .c-placeholder'))
        .find((el) => el.getBoundingClientRect().height > 0);
      if (!media) {
        stage.style.removeProperty('--nav-top');
        return;
      }
      const stageTop = stage.getBoundingClientRect().top;
      const box = media.getBoundingClientRect();
      let top = box.top - stageTop + box.height / 2;
      // Team cards stack contact buttons down the photo's right edge, and a
      // person with more routes has a taller stack. Keep the arrow (centred on
      // this line) clear below the tallest stack in the row.
      const stacks = Array.from(slider.querySelectorAll('.c-team__actions'));
      if (stacks.length) {
        const lowest = Math.max(...stacks.map((a) => a.getBoundingClientRect().bottom)) - stageTop;
        const half = (next.offsetHeight || 40) / 2;
        top = Math.max(top, lowest + 12 + half);
      }
      stage.style.setProperty('--nav-top', `${Math.round(top)}px`);
    };

    const update = () => {
      // A row that does not overflow is not a slider, so it gets no controls.
      const overflows = slider.scrollWidth - slider.clientWidth > 2;
      wrap.classList.toggle('c-sliderwrap--on', overflows);
      const max = slider.scrollWidth - slider.clientWidth;
      prev.disabled = !overflows || slider.scrollLeft <= 1;
      next.disabled = !overflows || slider.scrollLeft >= max - 1;

      if (overflows) {
        const unit = step(slider) || 1;
        const perView = Math.max(1, Math.round(slider.clientWidth / unit));
        const pages = Math.max(1, slider.children.length - perView + 1);
        const atEnd = slider.scrollLeft >= max - 1;
        const current = atEnd ? pages : Math.min(pages, Math.round(slider.scrollLeft / unit) + 1);
        count.textContent = `${current} / ${pages}`;
      }
    };

    const go = (direction) => {
      slider.scrollBy({
        left: step(slider) * direction,
        behavior: reduced() ? 'auto' : 'smooth',
      });
    };

    prev.addEventListener('click', () => go(-1));
    next.addEventListener('click', () => go(1));
    slider.addEventListener('scroll', update, { passive: true });
    // The photo's height only changes when the row resizes (or an image
    // loads), so the arrows are placed then, not on every scroll.
    new ResizeObserver(() => { placeArrows(); update(); }).observe(slider);

    placeArrows();
    update();
  };

  const scan = (root) => (root || document).querySelectorAll(SELECTOR).forEach(setup);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan());
  } else {
    scan();
  }

  // The theme editor replaces a section's markup in place.
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
})();
